import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { ShoppingBag, Heart, ArrowLeft, Minus, Plus, Share2, CheckCircle, Truck, RefreshCw, Shield, Star } from 'lucide-react';
import { addToCart, addToWishlist, removeFromWishlist, getToken, removeToken, removeUser } from '../api/auth';
import { useCurrency } from '../context/CurrencyContext';
const API = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const TRUST_BADGES = [
  { icon: CheckCircle, title: 'COD Available',   sub: 'Easy Returns & Quick Exchanges' },
  { icon: Truck,       title: 'Fast Delivery',    sub: '2–7 Business Days Across India' },
  { icon: RefreshCw,   title: 'Free Shipping',    sub: 'On Prepaid Orders Above ₹999' },
  { icon: Shield,      title: 'Premium Quality',  sub: 'Stones, Plating & Craftsmanship' },
];

const Toast = ({ msg, show }) => (
  <AnimatePresence>
    {show && (
      <motion.div className="pd-toast"
        initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 30 }} transition={{ duration: 0.28 }}>
        <CheckCircle size={13} /> {msg}
      </motion.div>
    )}
  </AnimatePresence>
);

const MiniCard = ({ product }) => {
  const navigate  = useNavigate();
  const { formatPrice } = useCurrency();
  const hasDiscount  = product.sale_price && product.sale_price < product.original_price;
  const displayPrice = hasDiscount ? product.sale_price : product.original_price;
  const mrpPrice     = hasDiscount ? product.original_price : null;

  return (
    <div className="pd-mini-card" onClick={() => navigate(`/products/${product.id}`)}>
      <div className="pd-mini-img">
        <img src={product.image_1 || product.images?.[0]} alt={product.name} loading="lazy" />
      </div>
      <div className="pd-mini-info">
        <p className="pd-mini-name">{product.name}</p>
        <div className="pd-mini-price-row">
          <span className="pd-mini-price">{formatPrice(displayPrice)}</span>
          {mrpPrice && <span className="pd-mini-mrp">{formatPrice(mrpPrice)}</span>}
        </div>
      </div>
    </div>
  );
};

const ProductDetails = () => {
  const { productId } = useParams();
  const location      = useLocation();
  const navigate      = useNavigate();
  const sectionRef    = useRef(null);
  const sectionInView = useInView(sectionRef, { once: true, margin: '-60px' });
  const { formatPrice } = useCurrency();

  // Breadcrumb info passed from CollectionDetail
  const collectionName = location.state?.collectionName || null;
  const collectionSlug = location.state?.collectionSlug || null;

  const [product, setProduct]               = useState(null);
  const [related, setRelated]               = useState([]);
  const [loading, setLoading]               = useState(true);
  const [error, setError]                   = useState('');
  const [activeImg, setActiveImg]           = useState(0);
  const [qty, setQty]                       = useState(1);
  const [wished,      setWished]      = useState(false);
  const [wishLoading, setWishLoading] = useState(false);
  const [toast, setToast]                   = useState({ show: false, msg: '' });
  const [selectedColor, setSelectedColor] = useState('');
  const [selectedSize,  setSelectedSize]  = useState('');

 useEffect(() => {
  const load = async () => {
    setLoading(true);
    setError('');
    setActiveImg(0);
    setQty(1);
    setRelated([]);
    window.scrollTo({ top: 0, behavior: 'smooth' });

    try {
      // Step 1 — load product
      const res  = await fetch(`${API}/products/${productId}`);
      const data = await res.json();

      if (!data.success) { setError('Product not found.'); setLoading(false); return; }

      setProduct(data.product);
      document.title = `${data.product.name} | Aamira`;

      // Step 2 — get slug
      const slug = collectionSlug || data.product.collection_slugs?.[0];

      if (!slug) { setLoading(false); return; }

      // Step 3 — load related products
      const res2  = await fetch(`${API}/products?collection=${slug}&active=true`);
      const data2 = await res2.json();

      if (data2.success) {
        const others = data2.products.filter(p => String(p.id) !== String(data.product.id)).slice(0, 4);
        setRelated(others);
      }

    } catch (err) {
      setError('Failed to load product.');
    } finally {
      setLoading(false);
    }
  };

  load();
}, [productId]);

  const showToast = (msg) => {
    setToast({ show: true, msg });
    setTimeout(() => setToast({ show: false, msg: '' }), 2200);
  };



  
const toggleWishlist = async () => {
  if (!getToken()) { navigate('/account'); return; }
  if (wishLoading) return;
  setWishLoading(true);
  try {
    if (wished) {
      await removeFromWishlist(product.id);
      setWished(false);
      showToast('Removed from wishlist');
    } else {
      await addToWishlist(product.id);
      setWished(true);
      showToast('Added to wishlist!');
    }
  } catch (err) {
    showToast(err.message || 'Wishlist error');
  } finally {
    setWishLoading(false);
  }
};

  if (loading) return (
    <div style={{ minHeight: '100vh', paddingTop: 162, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Jost, sans-serif', color: '#9A9186', letterSpacing: '0.1em' }}>
      Loading…
    </div>
  );

  if (error || !product) return (
    <div style={{ minHeight: '100vh', paddingTop: 162, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Jost, sans-serif', color: '#C0392B' }}>
      {error || 'Product not found.'}
    </div>
  );


  const handleAddToCart = async () => {
  if (product.stock_qty === 0) { showToast('This product is sold out'); return; }
  if (!getToken()) { navigate('/account'); return; }
  const colorCount = [...new Set(product.colors?.filter(c => c && c.trim()) ?? [])].length;
  const sizeCount  = [...new Set(product.sizes?.filter(s => s && s.trim()) ?? [])].length;

  if (colorCount > 1 && !selectedColor) {
    showToast('Please select a color'); return;
  }
  if (sizeCount > 1 && !selectedSize) {
    showToast('Please select a size'); return;
  }
  try {
    await addToCart(product.id, qty, selectedColor || null, selectedSize || null);
    showToast(`${product.name} added to cart!`);
    window.dispatchEvent(new Event('cart-updated'));
  } catch (err) {
    const msg = err.message?.toLowerCase() || '';
    if (msg.includes('invalid') || msg.includes('expired') || msg.includes('token') || msg.includes('unauthorized')) {
      removeToken(); removeUser(); navigate('/account'); return;
    }
    showToast(err.message || 'Failed to add to cart');
  }
};

  
  const rawImages = typeof product.images === 'string'
  ? JSON.parse(product.images)
  : product.images;

const gallery = (
  rawImages?.length
    ? rawImages
    : [product.image_1, product.image_2, product.image_3]
).filter(Boolean);
  const hasDiscount  = product.sale_price && product.sale_price < product.original_price;
  const displayPrice = hasDiscount ? product.sale_price : product.original_price;
  const mrpPrice     = hasDiscount ? product.original_price : null;
  const discount     = mrpPrice ? Math.round((1 - displayPrice / mrpPrice) * 100) : 0;

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,600;0,700;1,400&family=Jost:wght@300;400;500;600&family=Cormorant+Garamond:ital,wght@0,300;1,300&display=swap');
        .pd-page { background: #FAF8F4; min-height: 100vh; padding-top: 162px; font-family: 'Jost', sans-serif; position: relative; overflow-x: hidden; }
        .pd-breadcrumb { position: relative; z-index: 1; padding: 14px clamp(20px,5vw,64px); display: flex; align-items: center; gap: 8px; font-size: 10px; font-weight: 500; letter-spacing: 0.12em; text-transform: uppercase; color: #8A7E72; border-bottom: 1px solid rgba(176,141,87,0.12); flex-wrap: wrap; }
        .pd-breadcrumb a { color: #8A7E72; text-decoration: none; transition: color 0.2s; }
        .pd-breadcrumb a:hover { color: #B08D57; }
        .pd-breadcrumb-sep { color: rgba(176,141,87,0.5); }
        .pd-breadcrumb-current { color: #1C1A18; }
        .pd-main { position: relative; z-index: 1; max-width: 1200px; margin: 0 auto; padding: 40px clamp(20px,5vw,64px); display: grid; grid-template-columns: 1fr 1fr; gap: clamp(32px, 5vw, 72px); align-items: start; }
        .pd-gallery { position: sticky; top: 180px; }
        .pd-main-img { width: 100%; aspect-ratio: 1/1; overflow: hidden; border-radius: 2px; background: #F0EAE0; margin-bottom: 12px; border: 1px solid rgba(176,141,87,0.12); }
        .pd-main-img img { width: 100%; height: 100%; object-fit: cover; display: block; transition: transform 0.6s ease; }
        .pd-main-img:hover img { transform: scale(1.04); }
        .pd-thumbs { display: grid; grid-template-columns: repeat(4,1fr); gap: 8px; }
        .pd-thumb { aspect-ratio: 1/1; overflow: hidden; border-radius: 2px; border: 1.5px solid transparent; cursor: pointer; background: #F0EAE0; transition: border-color 0.2s; }
        .pd-thumb--active { border-color: #B08D57; }
        .pd-thumb img { width:100%; height:100%; object-fit:cover; display:block; transition: transform 0.4s; }
        .pd-thumb:hover img { transform: scale(1.06); }
        .pd-info { padding-top: 4px; }
        .pd-category { font-size: 9px; font-weight: 600; letter-spacing: 0.25em; text-transform: uppercase; color: #B08D57; display: flex; align-items: center; gap: 8px; margin-bottom: 10px; }
        .pd-category::before { content:''; width:20px; height:1px; background:#B08D57; }
        .pd-title { font-family: 'Playfair Display', serif; font-size: clamp(22px, 3vw, 34px); font-weight: 700; color: #1C1A18; line-height: 1.18; letter-spacing: -0.01em; margin-bottom: 14px; }
        .pd-stars { display: flex; align-items: center; gap: 6px; margin-bottom: 16px; }
        .pd-stars-icons { display:flex; gap:2px; }
        .pd-review-count { font-size: 11px; color: #8A7E72; }
        .pd-price-block { margin-bottom: 18px; }
        .pd-price { font-family: 'Jost', sans-serif; font-size: clamp(24px, 3vw, 32px); font-weight: 600; color: #B08D57; letter-spacing: 0.04em; }
        .pd-price-row { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; margin-bottom: 4px; }
        .pd-mrp { font-size: 14px; color: #AEA39A; text-decoration: line-through; font-weight: 300; }
        .pd-discount-badge { font-size: 9px; font-weight: 700; letter-spacing: 0.12em; text-transform: uppercase; color: #FAF8F4; background: #C0392B; padding: 3px 8px; border-radius: 1px; }
        .pd-tax-note { font-size: 10px; color: #8A7E72; margin-top: 2px; }
        .pd-divider { width: 100%; height: 1px; background: linear-gradient(to right, rgba(176,141,87,0.25), transparent); margin: 18px 0; }
        .pd-desc { font-size: 13px; font-weight: 300; color: #5C5247; line-height: 1.85; letter-spacing: 0.02em; margin-bottom: 20px; }
        .pd-stock { display: flex; align-items: center; gap: 7px; font-size: 11px; font-weight: 500; color: #C0392B; margin-bottom: 18px; }
        .pd-stock-dot { width: 7px; height: 7px; border-radius: 50%; background: #C0392B; flex-shrink: 0; animation: pd-pulse 1.4s ease-in-out infinite; }
        @keyframes pd-pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }
        .pd-qty-label { font-size: 9px; font-weight: 600; letter-spacing: 0.22em; text-transform: uppercase; color: #1C1A18; margin-bottom: 8px; }
        .pd-qty-ctrl { display: flex; align-items: center; border: 1px solid rgba(176,141,87,0.3); width: fit-content; margin-bottom: 20px; }
        .pd-qty-btn { width: 36px; height: 36px; background: none; border: none; cursor: pointer; color: #5C5247; display: flex; align-items: center; justify-content: center; transition: background 0.2s, color 0.2s; }
        .pd-qty-btn:hover { background: #B08D57; color: #FAF8F4; }
        .pd-qty-val { width: 40px; text-align: center; font-family: 'Playfair Display', serif; font-size: 15px; font-weight: 600; color: #1C1A18; border-left: 1px solid rgba(176,141,87,0.2); border-right: 1px solid rgba(176,141,87,0.2); padding: 8px 0; }
        .pd-actions { display: flex; flex-direction: column; gap: 10px; margin-bottom: 20px; }
        .pd-btn-atc { width: 100%; background: transparent; color: #1C1A18; border: 1.5px solid #1C1A18; font-family: 'Jost', sans-serif; font-size: 10px; font-weight: 600; letter-spacing: 0.22em; text-transform: uppercase; padding: 15px; cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 8px; position: relative; overflow: hidden; transition: color 0.28s; }
        .pd-btn-atc::after { content:''; position:absolute; inset:0; background:#1C1A18; transform:translateX(-101%); transition: transform 0.28s cubic-bezier(0.4,0,0.2,1); z-index:0; }
        .pd-btn-atc:hover { color:#FAF8F4; }
        .pd-btn-atc:hover::after { transform:translateX(0); }
        .pd-btn-atc > * { position:relative; z-index:1; }
        .pd-btn-buy { width: 100%; background: #1C1A18; color: #FAF8F4; border: none; font-family: 'Jost', sans-serif; font-size: 10px; font-weight: 600; letter-spacing: 0.22em; text-transform: uppercase; padding: 16px; cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 8px; position: relative; overflow: hidden; transition: color 0.28s; }
        .pd-btn-buy::after { content:''; position:absolute; inset:0; background:#B08D57; transform:translateX(-101%); transition: transform 0.28s cubic-bezier(0.4,0,0.2,1); z-index:0; }
        .pd-btn-buy:hover::after { transform:translateX(0); }
        .pd-btn-buy > * { position:relative; z-index:1; }
        .pd-icon-row { display: flex; gap: 10px; margin-bottom: 22px; }
        .pd-icon-btn { flex: 1; display: flex; align-items: center; justify-content: center; gap: 7px; padding: 10px; font-size: 9px; font-weight: 600; letter-spacing: 0.16em; text-transform: uppercase; color: #5C5247; border: 1px solid rgba(176,141,87,0.25); background: none; cursor: pointer; transition: border-color 0.2s, color 0.2s; }
        .pd-icon-btn:hover { border-color: #B08D57; color: #B08D57; }
        .pd-icon-btn--wished { color: #B08D57; border-color: #B08D57; }
        .pd-trust-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
        .pd-trust-card { display: flex; align-items: flex-start; gap: 10px; padding: 12px; background: #F5F0E8; border: 1px solid rgba(176,141,87,0.15); border-radius: 2px; }
        .pd-trust-icon { color: #B08D57; flex-shrink: 0; margin-top: 1px; }
        .pd-trust-title { font-size: 10px; font-weight: 600; color: #1C1A18; margin-bottom: 2px; }
        .pd-trust-sub { font-size: 10px; font-weight: 300; color: #8A7E72; line-height: 1.5; }
        .pd-bottom { position: relative; z-index: 1; max-width: 1200px; margin: 0 auto; padding: 0 clamp(20px,5vw,64px) 80px; }
        .pd-section-title { font-family: 'Playfair Display', serif; font-size: clamp(20px, 2.5vw, 28px); font-weight: 700; color: #1C1A18; display: flex; align-items: center; gap: 14px; margin-bottom: 22px; }
        .pd-section-title em { font-style: italic; font-weight: 400; color: #B08D57; }
        .pd-section-title::after { content: ''; flex:1; height:1px; background: linear-gradient(to right, rgba(176,141,87,0.3), transparent); }
        .pd-mini-grid { display: grid; grid-template-columns: repeat(4,1fr); gap: 16px; }
        .pd-mini-card { cursor: pointer; transition: transform 0.3s; }
        .pd-mini-card:hover { transform: translateY(-3px); }
        .pd-mini-img { aspect-ratio: 1/1; overflow: hidden; border-radius: 2px; background: #F0EAE0; margin-bottom: 10px; border: 1px solid rgba(176,141,87,0.1); }
        .pd-mini-img img { width:100%; height:100%; object-fit:cover; display:block; transition: transform 0.5s; }
        .pd-mini-card:hover .pd-mini-img img { transform: scale(1.06); }
        .pd-mini-name { font-size: 12px; font-weight: 500; color: #1C1A18; margin-bottom: 5px; line-height: 1.35; }
        .pd-mini-price-row { display:flex; align-items:center; gap:6px; }
        .pd-mini-price { font-family: 'Jost', sans-serif; font-size: 14px; font-weight: 600; color: #B08D57; letter-spacing: 0.04em; }
        .pd-mini-mrp { font-size: 11px; color: #AEA39A; text-decoration: line-through; }
        .pd-sec-divider { height: 1px; background: linear-gradient(to right, transparent, rgba(176,141,87,0.2) 20%, rgba(176,141,87,0.2) 80%, transparent); margin: 40px 0; }
        .pd-toast { position: fixed; bottom: 80px; left: 20px; right: 20px; width: auto; background: #1C1A18; color: #FAF8F4; font-family: 'Jost', sans-serif; font-size: 11px; font-weight: 500; letter-spacing: 0.1em; padding: 12px 22px; display: flex; align-items: center; justify-content: center; gap: 8px; z-index: 9999; border-radius: 1px; box-shadow: 0 8px 32px rgba(0,0,0,0.22); text-align: center; }
        @media (max-width: 860px) { .pd-main { grid-template-columns: 1fr; gap: 28px; } .pd-gallery { position: static; } .pd-mini-grid { grid-template-columns: repeat(2,1fr); } .pd-page { padding-top: 148px; } }
        @media (max-width: 480px) { .pd-trust-grid { grid-template-columns: 1fr; } .pd-page { padding-top: 136px; } }
      `}</style>

      <div className="pd-page">

        {/* Breadcrumb */}
        <div className="pd-breadcrumb">
          <Link to="/">Home</Link>
          <span className="pd-breadcrumb-sep">›</span>
          <Link to="/collections">Collections</Link>
          {collectionSlug && collectionName && (
            <>
              <span className="pd-breadcrumb-sep">›</span>
              <Link to={`/collections/${collectionSlug}`}>{collectionName}</Link>
            </>
          )}
          <span className="pd-breadcrumb-sep">›</span>
          <span className="pd-breadcrumb-current">{product.name}</span>
        </div>

        {/* Main grid */}
        <div className="pd-main">

          {/* Gallery */}
          <motion.div className="pd-gallery"
            initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.7 }}>
            <div className="pd-main-img">
              <AnimatePresence mode="wait">
                <motion.img key={activeImg} src={gallery[activeImg]} alt={product.name}
                  initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.35 }} />
              </AnimatePresence>
            </div>
            <div className="pd-thumbs">
              {gallery.map((src, i) => (
                <div key={i} className={`pd-thumb ${i === activeImg ? 'pd-thumb--active' : ''}`}
                  onClick={() => setActiveImg(i)}>
                  <img src={src} alt={`view ${i+1}`} loading="lazy" />
                </div>
              ))}
            </div>
          </motion.div>

          {/* Info */}
          <motion.div className="pd-info"
            initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.7, delay: 0.1 }}>

            <p className="pd-category">{collectionName || 'Jewellery Collection'}</p>
            <h1 className="pd-title">{product.name}</h1>

            <div className="pd-stars">
              <div className="pd-stars-icons">
                {[1,2,3,4,5].map(s => (
                  <Star key={s} size={13} fill={s <= 4 ? '#B08D57' : 'none'} color="#B08D57" />
                ))}
              </div>
              <span className="pd-review-count">4.0 (24 reviews)</span>
            </div>

            <div className="pd-price-block">
              <div className="pd-price-row">
                <span className="pd-price">{formatPrice(displayPrice)}</span>
                {mrpPrice && <span className="pd-mrp">{formatPrice(mrpPrice)}</span>}
                {discount > 0 && <span className="pd-discount-badge">{discount}% OFF</span>}
              </div>
              <p className="pd-tax-note">Inclusive of all taxes · Free shipping on prepaid</p>
            </div>

            <div className="pd-divider" />

            {product.description && <p className="pd-desc">{product.description}</p>}

            {product.offer_text && (
              <div style={{ background: 'rgba(176,141,87,0.1)', border: '1px solid rgba(176,141,87,0.25)', padding: '10px 14px', fontSize: 12, color: '#B08D57', marginBottom: 18, fontWeight: 500 }}>
                🏷 {product.offer_text}
              </div>
            )}


            {/* Color selector — only if product has colors in DB */}
              {product.colors?.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <p className="pd-qty-label">Color</p>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {product.colors.map(c => (
                      <button key={c} onClick={() => setSelectedColor(c)} style={{
                        padding: '7px 16px', fontSize: 11, fontFamily: 'Jost,sans-serif',
                        fontWeight: 500, letterSpacing: '0.1em', cursor: 'pointer',
                        border: selectedColor === c ? '1.5px solid #B08D57' : '1px solid #d4c5a9',
                        background: selectedColor === c ? '#1C1A18' : 'transparent',
                        color: selectedColor === c ? '#FAF8F4' : '#3a3530',
                        transition: 'all 0.2s',
                      }}>
                        {c}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Size selector — only if product has sizes in DB */}
              {product.sizes?.length > 0 && (
                <div style={{ marginBottom: 18 }}>
                  <p className="pd-qty-label">Size</p>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {product.sizes.map(s => (
                      <button key={s} onClick={() => setSelectedSize(s)} style={{
                        padding: '7px 16px', fontSize: 11, fontFamily: 'Jost,sans-serif',
                        fontWeight: 500, letterSpacing: '0.1em', cursor: 'pointer',
                        border: selectedSize === s ? '1.5px solid #B08D57' : '1px solid #d4c5a9',
                        background: selectedSize === s ? '#1C1A18' : 'transparent',
                        color: selectedSize === s ? '#FAF8F4' : '#3a3530',
                        transition: 'all 0.2s',
                      }}>
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              )}

            
            {product.stock_qty === 0 ? (
            <div className="pd-stock" style={{ color: '#C0392B' }}>
              <div className="pd-stock-dot" style={{ background: '#C0392B' }} />
              Sold Out — Currently unavailable
            </div>
          ) : product.stock_qty <= 5 && product.stock_qty > 0 ? (
            <div className="pd-stock">
              <div className="pd-stock-dot" />
              Only {product.stock_qty} left in stock!
            </div>
          ) : null}

            <p className="pd-qty-label">Quantity</p>
            <div className="pd-qty-ctrl">
              <button className="pd-qty-btn" onClick={() => setQty(q => Math.max(1, q-1))}><Minus size={13} /></button>
              <span className="pd-qty-val">{qty}</span>
              <button className="pd-qty-btn" onClick={() => setQty(q => Math.min(product.stock_qty, q + 1))}><Plus size={13} /></button>
            </div>

            <div className="pd-actions">
              <button className="pd-btn-atc" onClick={handleAddToCart} disabled={product.stock_qty === 0}>
              <ShoppingBag size={13} />
              <span>{product.stock_qty === 0 ? 'Sold Out' : 'Add to Cart'}</span>
            </button>
              <button className="pd-btn-buy" disabled={product.stock_qty === 0} onClick={() => {
              if (product.stock_qty === 0) return;
              if (!getToken()) { navigate('/account'); return; }
             const colorCount = [...new Set(product.colors?.filter(c => c && c.trim()) ?? [])].length;
              const sizeCount  = [...new Set(product.sizes?.filter(s => s && s.trim()) ?? [])].length;

              if (colorCount > 1 && !selectedColor) {
                showToast('Please select a color'); return;
              }
              if (sizeCount > 1 && !selectedSize) {
                showToast('Please select a size'); return;
              }

              // ✅ Add this
              if (qty > product.stock_qty) {
                showToast(`Only ${product.stock_qty} units available`);
                return;
              }
              navigate('/checkout', {
                state: {
                  items: [{
                    id:    product.id,
                    name:  product.name,
                    price: product.sale_price ?? product.original_price,
                    image: product.images?.[0] || product.image_1,
                    qty,
                    color: selectedColor || null,
                    size:  selectedSize  || null,
                  }]
                }
              });
            }}>
              <span>Buy It Now</span>
            </button>
            </div>

            <div className="pd-icon-row">
              <button
  className={`pd-icon-btn ${wished ? 'pd-icon-btn--wished' : ''}`}
  onClick={toggleWishlist}
  disabled={wishLoading}
>
  <Heart size={13} fill={wished ? '#B08D57' : 'none'} />
  {wishLoading ? 'Please wait…' : wished ? 'Wishlisted' : 'Add to Wishlist'}
</button>
              <button className="pd-icon-btn" onClick={() => { navigator.clipboard?.writeText(window.location.href); showToast('Link copied!'); }}>
                <Share2 size={13} /> Share
              </button>
            </div>

            <div className="pd-divider" />

            <div className="pd-trust-grid">
              {TRUST_BADGES.map((b, i) => (
                <div key={i} className="pd-trust-card">
                  <b.icon size={16} className="pd-trust-icon" />
                  <div>
                    <p className="pd-trust-title">{b.title}</p>
                    <p className="pd-trust-sub">{b.sub}</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Related products */}
       {/* Related products */}
{related.length > 0 && (
  <div className="pd-bottom">
    <div className="pd-sec-divider" />
    <div>
      <h2 className="pd-section-title">You May Also <em>Like</em></h2>
      <div className="pd-mini-grid">
        {related.map(p => <MiniCard key={p.id} product={p} />)}
      </div>
    </div>
  </div>
)}
      </div>

      <Toast msg={toast.msg} show={toast.show} />
    </>
  );
};

export default ProductDetails;